import { getOperationName } from '@apollo/client/utilities';
import { UUID } from '@cmg/common';
import { SnackbarManager } from '@cmg/design-system';
import React from 'react';

import { InterestLevelInput } from '../../../../../../../../../graphql';
import { MyOrders_PublishedOfferingsDocument } from '../../../../../../../../my-orders/graphql/__generated__';
import { pendingOrderBookGridForcedRefreshVar } from '../../../../../../../../order-book';
import {
  SalesAndTradingIndicationDocument,
  SalesAndTradingIndicationQuery,
} from '../../../../../context/hooks/sales-and-trading/__generated__';
import { IndicationFormValues } from '../../../common/utils/IndicationSectionValidation';
import { useManageIndication_UpdateCoveredIndicationMutation } from '../../../graphql/__generated__';

export const refetchQueries = [
  getOperationName(MyOrders_PublishedOfferingsDocument)!,
  getOperationName(SalesAndTradingIndicationDocument)!,
];

export const useUpdateCoveredIndication = (
  offeringId: UUID,
  indication: SalesAndTradingIndicationQuery['coveredIndication'],
  onFinishEditing: () => void
) => {
  const [updateCoveredIndication] = useManageIndication_UpdateCoveredIndicationMutation();

  const handleUpdateCoveredIndication = React.useCallback(
    async (values: IndicationFormValues) => {
      try {
        await updateCoveredIndication({
          variables: {
            offeringId,
            indicationId: indication!.id,
            input: {
              instrumentId: values.instrumentId,
              currencyCode: values.currencyCode,
              interestLevels: values.interestLevels.map<InterestLevelInput>(
                ({
                  interestQuantity,
                  interestUnit,
                  limitPrice,
                  limitType,
                  coupon,
                  premium,
                  percentageOfPar,
                }) => ({
                  interestUnit: interestUnit!,
                  interestQuantity: interestQuantity!,
                  limitType,
                  limitPrice,
                  coupon,
                  premium,
                  percentageOfPar,
                })
              ),
            },
          },
          awaitRefetchQueries: true,
          refetchQueries,
        });

        SnackbarManager.success('Successfully updated Indication');
        pendingOrderBookGridForcedRefreshVar(true);
        onFinishEditing();
      } catch {
        SnackbarManager.error('Failed to update Indication');
      }
    },
    [indication, offeringId, onFinishEditing, updateCoveredIndication]
  );

  return [handleUpdateCoveredIndication];
};
